import { Card, CardContent } from "../../../client/components/ui/card";
import { CardSectionHeader } from "../../../client/components/ui/card-section-header";
import { useDashboardData } from "../DashboardDataContext";
import { pnlColor } from "../chart/chartColors";

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

export default function PipStatsWidget() {
  const { analyticsData, isDark, t } = useDashboardData();

  const tp = t.analytics.pips;

  if (!analyticsData || analyticsData.pipStats.tradeCount === 0) {
    return (
      <Card className="h-full">
        <CardSectionHeader label={tp.sectionTitle} isDark={isDark} />
        <CardContent className="h-full p-4">
          <EmptyState label={tp.noData} />
        </CardContent>
      </Card>
    );
  }

  const { pipStats } = analyticsData;
  const mutedText = isDark ? "widget-label" : "widget-label";
  const avgPipsColor = pnlColor(pipStats.avgPips);

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={tp.sectionTitle} isDark={isDark} />
      <CardContent className="px-3 pb-3 pt-1 flex-1 overflow-hidden min-h-0">
        <div className="grid grid-cols-3 gap-2 h-full" style={{ gridTemplateRows: "1fr 1fr" }}>
          {/* Avg Pips */}
          <div className="rounded-lg px-2 py-1.5 bg-muted/40 flex flex-col justify-center min-w-0">
            <p className={`text-[9px] font-semibold tracking-widest uppercase truncate ${mutedText}`}>{tp.avgPips}</p>
            <p className="text-base font-bold font-mono leading-tight truncate" style={{ color: avgPipsColor }}>
              {pipStats.avgPips >= 0 ? "+" : ""}{pipStats.avgPips.toFixed(1)}
            </p>
          </div>

          {/* Win/Loss ratio */}
          <div className="rounded-lg px-2 py-1.5 bg-muted/40 flex flex-col justify-center min-w-0">
            <p className={`text-[9px] font-semibold tracking-widest uppercase truncate ${mutedText}`}>{tp.pipRatio}</p>
            <p className={`text-base font-bold leading-tight truncate ${pipStats.pipWinLossRatio >= 1 ? "text-primary" : "text-destructive"}`}>
              {isFinite(pipStats.pipWinLossRatio) ? pipStats.pipWinLossRatio.toFixed(2) : "∞"}
            </p>
          </div>

          {/* Total pips won / lost */}
          <div className="rounded-lg px-2 py-1.5 bg-muted/40 flex flex-col justify-center min-w-0">
            <p className={`text-[9px] font-semibold tracking-widest uppercase truncate ${mutedText}`}>{tp.totalPipsWonLost}</p>
            <p className="text-sm font-bold font-mono leading-tight text-primary truncate">+{pipStats.totalPipsWon.toFixed(1)}</p>
            <p className="text-xs font-mono leading-tight text-destructive truncate">{pipStats.totalPipsLost.toFixed(1)}</p>
          </div>

          {/* Avg pips win */}
          <div className="rounded-lg px-2 py-1.5 bg-muted/40 flex flex-col justify-center min-w-0">
            <p className={`text-[9px] font-semibold tracking-widest uppercase truncate ${mutedText}`}>{tp.avgPipsWin}</p>
            <p className="text-base font-bold font-mono leading-tight text-primary truncate">+{pipStats.avgPipsWin.toFixed(1)}</p>
          </div>

          {/* Avg pips loss */}
          <div className="rounded-lg px-2 py-1.5 bg-muted/40 flex flex-col justify-center min-w-0">
            <p className={`text-[9px] font-semibold tracking-widest uppercase truncate ${mutedText}`}>{tp.avgPipsLoss}</p>
            <p className="text-base font-bold font-mono leading-tight text-destructive truncate">{pipStats.avgPipsLoss.toFixed(1)}</p>
          </div>

          {/* Best / Worst trade */}
          <div className="rounded-lg px-2 py-1.5 bg-muted/40 flex flex-col justify-center min-w-0">
            <p className={`text-[9px] font-semibold tracking-widest uppercase truncate ${mutedText}`}>{tp.bestWorstTrade}</p>
            <p className="text-sm font-bold font-mono leading-tight text-primary truncate">{pipStats.bestTradePips >= 0 ? "+" : ""}{pipStats.bestTradePips.toFixed(1)}</p>
            <p className="text-xs font-mono leading-tight text-destructive truncate">{pipStats.worstTradePips.toFixed(1)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
